<template>
  <div class="god_page_container">
    <h2>Talleres privados</h2>
    <div id="selector_container">
      <select 
        v-model="modo"
        @change="filterPlans()"
        class="input_200">
        <option value="0">Talleres vigentes</option>
        <option value="1">Talleres caducados</option>
        <option value="2">Talleres eliminados</option>
      </select>
      <input
        type="date"
        v-model="planDate"
        @change="filterPlans()"
        class="input_200"
        placeholder="Fecha del taller"
      />
      <input
        type="email"
        class="input_200"
        v-model="user_email"
        @input="filterPlans()"
        placeholder="Email del usuario"
      />

      <button @click="createNewPrivatePlan()" class="input_200 main_button">Crear nuevo taller privado</button>
    </div>
    <div class="table-container" >
      <table>
        <thead>
          <tr>
              <th class="small">ID</th>
              <th class="big">Titulo</th>
              <th class="small">Fecha</th>
              <th class="big">Empresa</th>
              <th class="small">Estado</th>
              <th class="small">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in filteredPlans"
            :key="item.id"
            :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          >
            <td>{{ item.id }}</td>
            <td>{{ item.titulo }}</td>
            <td>{{ formatFecha(item.fecha) }}</td>
            <td>{{ item.empresa_nombre }}</td>
            <td>{{ obtenerEstado(item.pago_inicial_estado, item.pago_final_estado) }}</td>
            <td>
              <img class="action_icon" src="@/assets/images/ic_reservation_edit.png" @click="onEditClicked(item)"/>
              <img class="action_icon" src="@/assets/images/ic_action_clone.png" @click="onCloneClicked(item)"/>
              <img class="action_icon" src="@/assets/images/ic_action_delete.png" @click="onHideClicked(item)"/>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="filteredPlans.length === 0" style="display: flex; justify-content: center; margin-top: 1rem;">
      <label>No hay ningún taller privado con ese estado</label>
    </div>

    <PrivatePlanDetailsPopup
      :privatePlan="selectedPrivatePlanDetails" 
      @privatePlanChanged="onPrivatePlanChanged"
      @privatePlanChangedWithoutClosing="onPrivatePlanChangedWithoutClosing"
      @privatePlanDetailsPopupCancelled="onPrivatePlanDetailsPopupCancelled"
      @privatePlanDetailsErrorHappened="onPrivatePlanDetailsErrorHappened"
      />

    <ErrorPopup :errorMessage="error" />
    <TwoButtonPopup
      :title="twoButtonTitle"
      :message="twoButtonMessage"
      :acceptButton="twoButtonAcceptButton"
      @acceptCallback="() => twoButtonConfirmed()"
      :cancelButton="twoButtonCancelButton"
      @cancelCallback="() => twoButtonCancelled()"
      />

  </div>

</template>

<script setup>

  import { ref, nextTick } from 'vue'
  import { useLoadingStore } from "@/stores/loadingStore";
  import { useRouter, useRoute } from 'vue-router';
  import { refreshToken } from "@/services/authService";
  import ErrorPopup from "@/components/common/ErrorPopup.vue";
  import TwoButtonPopup from "@/components/common/TwoButtonPopup.vue";
  import PrivatePlanDetailsPopup from './PrivatePlanDetailsPopup.vue';

  const error = ref(null);
  const plans = ref([]);
  const filteredPlans = ref([]);
  const loadingStore = useLoadingStore();
  const router = useRouter();
  const route = useRoute();
  const modo = ref(route.query.modo || '0');
  const planDate = ref(route.query.planDate || '');
  const user_email = ref(route.query.user_email || '');

  const twoButtonTitle = ref("");
  const twoButtonMessage = ref("");
  const twoButtonAcceptButton = ref("");
  const twoButtonCancelButton = ref("");
  let twoButtonConfirmed = null;
  let twoButtonCancelled = null;

  const selectedPrivatePlanDetails = ref(null);

  const getAllPrivatePlans = async () => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await getAllPrivatePlansInAPI();

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await getAllPrivatePlansInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        showError("Se ha producido un error buscando los talleres privados.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
        plans.value = [];
      } else {
        error.value = null;
        plans.value = data.planes;
      }
      filterPlans();
    } catch (exception) {
      error.value = exception.message;
    } finally {
      loadingStore.setLoading(false);
    }
  };

  const filterPlans = () => {
    const today = new Date().toISOString().split('T');

    filteredPlans.value = plans.value.filter((plan) => {
      const planDay = plan.fecha.split(' ')[0];
      
      if (planDate.value && planDay !== planDate.value) {
        return false;
      }

      if (user_email.value && !plan.usuario_email.toLowerCase().includes(user_email.value.toLowerCase())) {
        return false;
      }

      if (modo.value === '0') {
        return plan.activo === true && planDay >= today[0];
      } else if (modo.value === '1') {
        return plan.activo === true && planDay < today[0];
      } else {
        return plan.activo === false;
      }
    });
  }

  async function getAllPrivatePlansInAPI() {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/private_plans/get?user_id="+localStorage.getItem('user_id'), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
      });
  }

  function obtenerEstado(pago_inicial_estado, pago_final_estado) {
    if (pago_final_estado == 1) {
      return "Pagado";
    } else if (pago_inicial_estado == 1) {
      return "Pago inicial";
    } else {
      return "Pendiente";
    }
  }

  const formatFecha = (fechaOriginal) => {
    if (!fechaOriginal) return '';
    const parts = fechaOriginal.split(' ');
    if (parts.length !== 2) return fechaOriginal;
    
    const dateParts = parts[0].split('-');
    if (dateParts.length !== 3) return fechaOriginal;
    
    const timeParts = parts[1].split(':');
    if (timeParts.length < 2) return fechaOriginal;
    
    const year = dateParts[0];
    const month = parseInt(dateParts[1], 10);
    const day = parseInt(dateParts[2], 10);
    const hour = timeParts[0];
    const minute = timeParts[1];
    
    return `${day}/${month}/${year} - ${hour}:${minute}`;
  };

  const showError = async (message) => {
    error.value = null;
    await nextTick();
    error.value = message;
  };

  const onEditClicked = async (item) => {
    getPrivatePlanDetails(item.id, "edit");
  }

  const onCloneClicked = async (item) => {
    getPrivatePlanDetails(item.id, "clone");    
  }

  const onHideClicked = async (item) => {
    twoButtonTitle.value = item.activo ? "Eliminar taller privado" : "Activar taller privado";
    twoButtonMessage.value = item.activo ? "¿Estás seguro de que deseas eliminar este taller privado?" : "¿Estás seguro de que deseas activar de nuevo el taller privado?";
    twoButtonAcceptButton.value = "Aceptar";
    twoButtonCancelButton.value = "Cancelar";
    twoButtonConfirmed = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
      changePrivatePlanVisibilityConfirmed(item);
    };
    twoButtonCancelled = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
    }
  }

  const changePrivatePlanVisibilityConfirmed = async (item) => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await changePrivatePlanVisibilityInAPI(item);

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await changePrivatePlanVisibilityInAPI(item);
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        loadingStore.setLoading(false);
        showError("Se ha producido un error cambiando el estado del taller privado.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
      } else {
        error.value = null;
      }
      getAllPrivatePlans();
    } catch (exception) {
      error.value = exception.message;
    }
  };

  const getPrivatePlanDetails = async (planId, action) => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await getPrivatePlanDetailsInAPI(planId);

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await getPrivatePlanDetailsInAPI(planId);
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      if (!response.ok) {
        loadingStore.setLoading(false);
        showError("Se ha producido un error buscando los detalles del taller privado.");
        return;
      }

      loadingStore.setLoading(false);

      const data = await response.json();

      if (data.errors) {
        error.value = data.message;
      } else {
        error.value = null;
        if (action == "edit") {
          selectedPrivatePlanDetails.value = data.plan;
        } else if (action == "clone") {
          data.plan.id = null;
          data.plan.pago_inicial_estado = 0;
          data.plan.pago_final_estado = 0;
          selectedPrivatePlanDetails.value = data.plan;
        }
      }
    } catch (exception) {
      error.value = exception.message;
    }
  }

  const getPrivatePlanDetailsInAPI = async (planId) => {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/private_plans/get_details?user_id="+localStorage.getItem('user_id')+"&plan_id="+planId, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        }
      });
  }

  const createNewPrivatePlan = async () => {
    selectedPrivatePlanDetails.value = {
      id: null,
      titulo: "",
      observaciones: "",
      num_plazas: 0,
      precio_por_plaza: 0,
      precio_total: 0,
      fecha: "",
      direccion: "",
      ciudad: "",
      empresa_nombre: "",
      empresa_email: "",
      pago_inicial_estado: 0,
      pago_inicial_cantidad: 0,
      pago_final_estado: 0,
      pago_final_cantidad: 0,
      usuario_nombre: "",
      usuario_email: "",
      gge: 0,
      activo: true
    }
  }

  const changePrivatePlanVisibilityInAPI = async (item) => {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/private_plans/edit_visibility", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
        body: JSON.stringify({
          "user_id": localStorage.getItem('user_id'),
          "plan_id": item.id,
          "visibility": item.activo ? 0 : 1
        }),
      });
  };

  const onPrivatePlanDetailsPopupCancelled = () => {
    selectedPrivatePlanDetails.value = null;
  };

  const onPrivatePlanDetailsErrorHappened = (message) => {
    showError(message);
  };
  
  const onPrivatePlanChanged = () => {
    selectedPrivatePlanDetails.value = null;
    getAllPrivatePlans();
  }

  const onPrivatePlanChangedWithoutClosing = () => {
    getAllPrivatePlans();
  }

  getAllPrivatePlans();

</script>

<style scoped>

#selector_container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding-top: 1rem;
  justify-content: space-between;
}

.input_200 {
  box-sizing: border-box;
  min-width: 200px;
  flex: 1 1 200px;
  padding: 8px;
  width: 100%;
  line-height: 1.2;
  height: 50px;
  max-height: 50px;
}

.table-container {
  overflow-x: auto;
  width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 30px;
  min-width: 800px;
}

th {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
  background-color: var(--gray);
  color: white;
  border: 0px;
}

tfoot {
  background-color: var(--gray);
  color: white;
  border: 0px;
}
.big {
  width: 20%;
}

.small {
  width: 10%;
}

tr{
  cursor: pointer;
}

td {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
}

.bg-gray-100 {
  background-color: var(--almost-white);
}

.bg-white {
  background-color: white;
}

.action_icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin: 0px 5px;
}

.action_icon_white {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-left: 10px;
  filter: brightness(0) invert(1);
}

@media (max-width: 768px) {
  #selector_container > * {
    flex: none !important;
  }

  .input_200 {
    width: 100%;
  }

  table {
    width: 100%;
    min-width: 0px;
  }

}

</style>
